import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService } from 'primeng/api';

import { PersonalInfoState } from '../../../models/enums/peronsalInfoState';
import { ParentsRelation, ParentsRelationDescriptions } from '../../../models/enums/parentsRelation';
import { ParentsInformationRequest } from '../../../models/parents-information/parentsInformationRequest';
import { ParentsInformationResponse } from '../../../models/parents-information/parentsInformationResponse';
import { ParentsInformationService } from '../../../services/parents-information/parents-information.service';
import { AuthService } from '../../../services/account/auth.service';

@Component({
  selector: 'app-parents-information',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    DropdownModule
  ],
  templateUrl: './parents-information.component.html',
  styleUrls: ['./parents-information.component.scss']
})
export class ParentsInformationComponent implements OnInit {
  @Input() userId: string | undefined;
  @Input() userState: PersonalInfoState | undefined;
  @Input() visible: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() updateSuccess = new EventEmitter<void>();

  parentsInfoForm!: FormGroup;

  // Parent relation options
  parentRelations = [
    { name: ParentsRelationDescriptions[ParentsRelation.Father], value: ParentsRelation.Father },
    { name: ParentsRelationDescriptions[ParentsRelation.Mother], value: ParentsRelation.Mother },
    { name: ParentsRelationDescriptions[ParentsRelation.Guardian], value: ParentsRelation.Guardian },
    { name: ParentsRelationDescriptions[ParentsRelation.Other], value: ParentsRelation.Other }
  ];

  loading = false;
  existingParentsInfo: ParentsInformationResponse[] = [];
  isEditMode = false;
  editingParentId: number | null = null;

  // Services
  private readonly formBuilder = inject(FormBuilder);
  private readonly parentsInformationService = inject(ParentsInformationService);
  private readonly messageService = inject(MessageService);
  private readonly authService = inject(AuthService);

  constructor() {
    this.initializeParentsInfoForm();
  }

  ngOnInit(): void {
    this.prefillParentsInfoForm();
  }

  private initializeParentsInfoForm(): void {
    this.parentsInfoForm = this.formBuilder.group({
      relation: [null],
      name: [''],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.pattern(/^[+]?[\d\s\-\(\)]{10,}$/)]]
    });
  }

  public prefillParentsInfoForm(): void {
    if (this.userId) {
      // Load existing parents information
      this.loadExistingParentsInfo();
    }
  }

  private loadExistingParentsInfo(): void {
    if (!this.userId) return;

    this.parentsInformationService.getParentsInformationByStudentId(this.userId).subscribe({
      next: (parentsInfo) => {
        this.existingParentsInfo = parentsInfo.result;
        // If there's existing data, populate the form with the first parent's info
        if (parentsInfo.result.length > 0) {
          const firstParent = parentsInfo.result[0];
          this.parentsInfoForm.patchValue({
            relation: firstParent.relation ?? null,
            name: firstParent.name,
            email: firstParent.email,
            phone: firstParent.phone
          });
          this.isEditMode = true;
          this.editingParentId = firstParent.id;
        }
      },
      error: (err) => {
        console.error('Error loading parents information:', err);
        // Initialize with empty values if no existing data
        this.parentsInfoForm.patchValue({
          relation: null,
          name: '',
          email: '',
          phone: ''
        });
      }
    });
  }

  public updateParentsInfo(): void {
    if (this.parentsInfoForm.invalid) {
      this.parentsInfoForm.markAllAsTouched();
      return;
    }

    if (!this.userId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'User ID is required'
      });
      return;
    }

    this.loading = true;
    const formData = this.parentsInfoForm.value;

    const request: ParentsInformationRequest = {
      studentId: this.userId,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      relation: formData.relation
    };

    if (this.isEditMode && this.editingParentId) {
      // Update existing parent information
      this.parentsInformationService.updateParentsInformation(this.editingParentId, request).subscribe({
        next: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Parent information updated successfully'
          });
          this.updateSuccess.emit();
          this.closeParentsInfoDialog();
        },
        error: (err) => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err?.error?.message || 'Failed to update parent information'
          });
        }
      });
    } else {
      // Create new parent information
      this.parentsInformationService.createParentsInformation(request).subscribe({
        next: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Parent information created successfully'
          });
          this.updateSuccess.emit();
          this.closeParentsInfoDialog();
        },
        error: (err) => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err?.error?.message || 'Failed to create parent information'
          });
        }
      });
    }
  }

  public closeParentsInfoDialog(): void {
    this.close.emit();
  }

  public resetParentsInfo(): void {
    this.parentsInfoForm.reset();
    this.isEditMode = false;
    this.editingParentId = null;
    this.prefillParentsInfoForm();
  }

  public switchToCreateMode(): void {
    this.isEditMode = false;
    this.editingParentId = null;
    this.parentsInfoForm.reset();
  }

  public editParent(parentInfo: ParentsInformationResponse): void {
    this.isEditMode = true;
    this.editingParentId = parentInfo.id;
    this.parentsInfoForm.patchValue({
      relation: parentInfo.relation,
      name: parentInfo.name,
      email: parentInfo.email,
      phone: parentInfo.phone
    });
  }

  public getRelationDescription(relation: ParentsRelation): string {
    return ParentsRelationDescriptions[relation] || 'Unknown';
  }
}
